import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { CoreContext } from "../../context/global/CoreContext";
import axios from "axios";
import {
  getCurrentHostname,
  isAccessingViaIpAddress,
} from "../../utils/common/environment";

/**
 * A higher-order component that checks if the user is authenticated.
 * If the user is not authenticated, they are redirected to the login page.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components to render if the user is authenticated.
 * @param {boolean} [props.manager=false] - Whether the manager role is required.
 * @param {boolean} [props.executive=false] - Whether the executive role is required.
 * @returns {React.ReactNode} The child components if authenticated, otherwise redirects to login.
 */
const RequireAuth = ({ children, manager = false, executive = false }) => {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const { user, userLoading, userError, refreshUser } = useContext(CoreContext);

  const requiredRoles = [manager ? "manager" : null, executive ? "executive" : null].filter(Boolean);

  useEffect(() => {
    // Check if we're on a mobile device on local network (IP address rather than localhost)
    const isMobileOnLocalNetwork = isAccessingViaIpAddress();
    
    // Get hostname from URL
    const currentHost = getCurrentHostname();
    
    // Force axios to use the correct IP instead of 127.0.0.1 when on mobile
    if (isMobileOnLocalNetwork && axios.defaults.baseURL?.includes('127.0.0.1')) {
      // Replace 127.0.0.1 with the actual IP address
      axios.defaults.baseURL = axios.defaults.baseURL.replace('127.0.0.1', currentHost);
    }
    
    const checkAuth = async () => {
      try {
        // If user data is already loaded from context
        if (user && !userLoading) {
          const { executive: isExecutive, manager: isManager } = user;

          // Map roles to booleans
          const userRoles = {
            executive: isExecutive,
            manager: isManager,
          };

          // If no required roles are specified, allow access if the user exists
          const hasAccess =
            requiredRoles.length === 0 ||
            requiredRoles.some((role) => userRoles[role]);

          if (hasAccess) {
            setIsAuthorized(true);
          } else {
            navigate("/not-authorized"); // Redirect to a not authorized page
          }
        } 
        // If there was an error loading the user or the user isn't loaded yet
        else if (userError || !user) {
          try {
            // Try to refresh the user data
            if (!userLoading) {
              console.log("Attempting to refresh user with correct API URL");
              const userData = await refreshUser();
              // Check access with refreshed data
              const { executive: isExecutive, manager: isManager } = userData;
              
              const userRoles = {
                executive: isExecutive,
                manager: isManager,
              };
              
              const hasAccess =
                requiredRoles.length === 0 ||
                requiredRoles.some((role) => userRoles[role]);
              
              if (hasAccess) {
                setIsAuthorized(true);
              } else {
                navigate("/not-authorized");
              }
            }
          } catch (error) {
            // If refreshing fails, redirect to login
            const currentPath = window.location.pathname + window.location.search;
            
            navigate(`/login?next=${encodeURIComponent(currentPath)}`);
          }
        }
      } catch (error) {
        const currentPath = window.location.pathname + window.location.search;
        
        navigate(`/login?next=${encodeURIComponent(currentPath)}`);
      }
    };
    
    checkAuth();
  }, [navigate, requiredRoles, user, userLoading, userError, refreshUser]);
  // Show nothing while checking authorization
  if (userLoading) {
    return null; // Or a loading spinner if preferred
  }
  
  return isAuthorized ? children : null;
};

export default RequireAuth;
